import { useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { ChevronDown, Check } from "@untitledui/icons";
import { cx } from "@/utils/cx";

interface Option {
  label: string;
  value: string;
}

interface StyledSelectProps {
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
}

export function StyledSelect({ options, value, onChange, label, placeholder = "Select..." }: StyledSelectProps) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);

  return (
    <div className="flex items-center gap-2">
      {label && <span className="text-sm text-text-tertiary whitespace-nowrap">{label}</span>}
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button
            type="button"
            className={cx(
              "inline-flex items-center gap-2 rounded-lg border border-border-secondary bg-bg-primary px-3 h-10 text-sm",
              "hover:bg-bg-secondary_hover transition-colors",
              "focus:outline-none focus:ring-2 focus:ring-fg-brand-primary focus:ring-offset-1",
              selected ? "text-text-primary" : "text-text-tertiary",
            )}
          >
            <span>{selected?.label ?? placeholder}</span>
            <ChevronDown className={cx("size-4 text-text-tertiary transition-transform", open && "rotate-180")} />
          </button>
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content
            sideOffset={8}
            align="start"
            className="z-50 min-w-[140px] rounded-xl border border-border-secondary bg-bg-primary p-1.5 shadow-lg animate-in fade-in-0 zoom-in-95"
          >
            {options.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                className={cx(
                  "flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm transition-colors",
                  opt.value === value
                    ? "bg-bg-active text-text-primary font-medium"
                    : "text-text-secondary hover:bg-bg-secondary_hover hover:text-text-primary",
                )}
              >
                <span>{opt.label}</span>
                {opt.value === value && <Check className="size-4 text-fg-brand-primary" />}
              </button>
            ))}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
}
